import { Card, Pagination } from "antd";
import SearchInput from "../../components/Forms/SearchInput";
import { useCallback, useEffect, useRef, useState } from "react";
import { roleIds } from "../../constant";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { UpOutlined } from "@ant-design/icons";
import { useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import Loader from "../../common/Loader";
import Filters from "../../components/Filters";
import { calcInStock, getStockStatus, handleRouteNav } from "../../utils";
import CustomBadge from "../../components/Charts/CustomBadge";
import { PiHeartFill } from "react-icons/pi";

const CategoryProducts = () => {
  const { role_id, customer_id, is_customer } = useSelector(
    (state) => state.auth,
  );
  const { category } = useParams();
  const defaultCategory = category;
  const [categoryData, setCategoryData] = useState([]);
  const { appliedFilters, DrawerUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "category",
        label: "Category",
        apiConfig: {
          apiRoute: "category",
          handler: (response) => {
            const { data = [] } = response;
            setCategoryData(data);
            return data.map((item) => ({
              label: item.category_clienttype,
              value: item.category_clienttype,
            }));
          },
          method: "POST",
        },
      },
      {
        type: "checkbox",
        key: "inStock",
        label: "In Stock",
      },
      {
        type: "checkbox",
        key: "isWishlist",
        label: "Favorites",
      },
      // {
      //   type: "range",
      //   key: "price",
      //   label: "Price Range",
      //   min: 0,
      //   max: 300,
      //   step: 1,
      //   marks: {
      //     0: "₹0",
      //     50: "₹50",
      //     100: "₹100",
      //     150: "₹150",
      //     200: "₹200",
      //     300: "₹300",
      //   },
      //   formatter: (val) => `₹${val}`,
      //   // formProps: { style: { marginTop: "30px" } },
      // },
      {
        type: "select",
        key: "width",
        label: "Width",
        options: [
          { label: '36"', value: "36" },
          { label: '54"', value: "54" },
          { label: '58"', value: "58" },
        ],
      },
      // {
      //   type: "select",
      //   key: "favCount",
      //   label: "Favorite Count",
      //   options: [
      //     { label: "0", value: "0" },
      //     { label: "1 - 2", value: "1-2" },
      //     { label: "3 - 4", value: "3-4" },
      //     { label: "5 - 10", value: "5-10" },
      //     { label: "> 10", value: "10+" },
      //   ],
      // },
    ],
    onChange: () => {
      setPage(1);
      setProductData([]);
    },
  });

  const { showPopup } = useGlobalModal();
  const { postApi, loading } = useApiCall();
  const [setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [productData, setProductData] = useState([]);
  const handleChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPerPage(pagination.pageSize);
  };

  const user_id = useSelector((state) => state.auth.user_id);
  const full_name = useSelector((state) => state.auth.full_name);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentItem, setCurrentItem] = useState(null);

  const query = searchParams.get("q") || "";
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [totalProducts, setTotalProduct] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isCategory, setIsCategoryValid] = useState(false);
  const loaderRef = useRef(null);

  const { setTitle, setDescription, setBackButton } = useOutletContext();
  const fetchIdRef = useRef(0);

  const fetchProducts = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;

    const isCategoryValid = categoryData.some(
      (item) => item.category_clienttype === defaultCategory,
    );
    setIsCategoryValid(isCategoryValid);
    if (!isCategoryValid && defaultCategory) {
      navigate("/");
      return;
    }

    try {
      setIsFetchingMore(true);
      const { inStock, width, price, category, isWishlist, favCount } =
        appliedFilters;

      const filters = {
        inStock: inStock ? "yes" : undefined,
        isWishlist: isWishlist ? "yes" : undefined,
        finish_width: width,
        minRate: price?.[0],
        maxRate: price?.[1],
        clienttype: category,
        wishlistRange: favCount,
      };

      const response = await postApi("product", {
        search: query,
        per_page: perPage,
        page,
        deviceType: "Web",
        customer_id: customer_id,
        clienttype: defaultCategory,
      });

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      if (!response?.status) {
        setProductData([]);
        setTotalProduct(0);
        setHasMore(false);
        return;
      }
      const formattedRows = (response?.data || []).map((item) => ({
        ...item,
        productImageUrl: item.product?.product_pic_url,
      }));
      setProductData((prev) =>
        page === 1 ? formattedRows : [...prev, ...formattedRows],
      );
      setTotalProduct(response?.total || 0);
      setHasMore(formattedRows.length === perPage);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
    } finally {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setIsFetchingMore(false);
    }
  }, [categoryData, customer_id, navigate, page, perPage, query]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchProducts]);

  const handleRowClick = (record) => {
    navigate(handleRouteNav(`/product-details/${record.sort_no}`, role_id));
  };

  const onSearch = (value, _e, info) =>
    console.log(info === null || info === void 0 ? void 0 : info.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPage(1);
  };

  const toggleFav = (e, index) => {
    e.stopPropagation();
    e.preventDefault();

    setProductData((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const isCurrentlyFav = item?.product?.wishlist_tag === "yes";

          return {
            ...item,
            product: {
              ...item.product,
              wishlist_tag: isCurrentlyFav ? "no" : "yes",
            },
          };
        }
        return item;
      }),
    );

    const item = productData[index];
    if (item?.product?.wishlist_tag === "yes") {
      rmoveWishlistItem(item);
    } else {
      addWishlistItem(item);
    }
  };

  const addWishlistItem = async (item) => {
    const payload = {
      customer_id: customer_id,
      product_id: item?.product?.product_id,
      sort_no: item?.product?.sort_no,
    };
    const res = await postApi("wishlist/add-wishlist", payload);
  };

  const rmoveWishlistItem = async (item) => {
    const payload = {
      customer_id: customer_id,
      product_id: item?.product?.product_id,
      sort_no: item?.product?.sort_no,
    };
    const res = await postApi("wishlist/delete-wishlist-product", payload);
  };

  useEffect(() => {
    setPage(0);
  }, [query, appliedFilters, defaultCategory]);

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loading &&
          !isFetchingMore
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 },
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [hasMore, loading, isFetchingMore]);

  useEffect(() => {
    if (!is_customer) {
      setTitle("Product Catalogue");
      setDescription(`${totalProducts} total products`);
      setBackButton(false);
    } else if (defaultCategory) {
      setTitle(isCategory ? defaultCategory : "");
      setBackButton(!!defaultCategory);
    } else {
      setTitle(null);
      setDescription(null);
      setBackButton(false);
    }
  }, [
    setTitle,
    setDescription,
    setBackButton,
    totalProducts,
    defaultCategory,
    isCategory,
  ]);

  return (
    <>
      <DrawerUI />
      <>
        <div
          className="bg-[#FFFFFF]"
          style={{ borderRadius: "32px", overflow: "hidden" }}
        >
          {!defaultCategory && (
            <div className="flex justify-between gap-7 items-center px-8 py-6">
              <SearchInput
                onSearch={onSearch}
                value={query}
                onChange={handleOnSearchChange}
                placeholder="Search for products"
              />
              <div className="flex gap-3 items-center">
                <FilterButton />
              </div>
            </div>
          )}

          <>
            {productData?.length > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6">
                  {productData.map((item, i) => {
                    const { key, text, variant } = getStockStatus(
                      item.quality_shade,
                    );
                    return (
                      <Card
                        key={i}
                        hoverable
                        variant="outlined"
                        style={{
                          borderRadius: 14,
                          overflow: "hidden",
                        }}
                        onClick={() => handleRowClick(item)}
                        cover={
                          <div style={{ position: "relative" }}>
                            <div className="p-3">
                              <img
                                src={
                                  item.product?.product_pic_url ||
                                  "/assets/images/place_holder_image1.svg"
                                }
                                onError={(e) =>
                                  (e.currentTarget.src =
                                    "/assets/images/place_holder_image1.svg")
                                }
                                className="rounded-xl w-full h-48 object-cover"
                              />
                            </div>

                            {/* Status Pill */}
                            <div
                              style={{
                                position: "absolute",
                                top: 24,
                                left: 24,
                              }}
                            >
                              <CustomBadge
                                dotClassName="mr-3"
                                customColors={{
                                  bgColor: "#00000066",
                                  textColor: "#fff",
                                }}
                                variant={variant}
                                text={text}
                              />
                            </div>

                            {/* Favorite Icon */}
                            <div
                              onClick={(e) => toggleFav(e, i)}
                              style={{
                                position: "absolute",
                                top: 24,
                                right: 24,
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                              }}
                              className="border border-transparent hover:border-[#A33434] transition-all duration-200"
                            >
                              {item?.product?.wishlist_tag == "yes" ? (
                                <svg
                                  width="21"
                                  height="18"
                                  viewBox="0 0 21 18"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M21 5.8125C21 12.375 11.2697 17.6869 10.8553 17.9062C10.7461 17.965 10.624 17.9958 10.5 17.9958C10.376 17.9958 10.2539 17.965 10.1447 17.9062C9.73031 17.6869 0 12.375 0 5.8125C0.00173694 4.27146 0.614681 2.79404 1.70436 1.70436C2.79404 0.614681 4.27146 0.00173694 5.8125 0C7.74844 0 9.44344 0.8325 10.5 2.23969C11.5566 0.8325 13.2516 0 15.1875 0C16.7285 0.00173694 18.206 0.614681 19.2956 1.70436C20.3853 2.79404 20.9983 4.27146 21 5.8125Z"
                                    fill="#A33434"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="21"
                                  height="18"
                                  viewBox="0 0 21 18"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    opacity="0.1"
                                    d="M21 5.8125C21 12.375 11.2697 17.6869 10.8553 17.9062C10.7461 17.965 10.624 17.9958 10.5 17.9958C10.376 17.9958 10.2539 17.965 10.1447 17.9062C9.73031 17.6869 0 12.375 0 5.8125C0.00173694 4.27146 0.614681 2.79404 1.70436 1.70436C2.79404 0.614681 4.27146 0.00173694 5.8125 0C7.74844 0 9.44344 0.8325 10.5 2.23969C11.5566 0.8325 13.2516 0 15.1875 0C16.7285 0.00173694 18.206 0.614681 19.2956 1.70436C20.3853 2.79404 20.9983 4.27146 21 5.8125Z"
                                    fill="black"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        }
                      >
                        {/* Bottom Card Content */}
                        <div className="flex justify-between mb-2 text-start">
                          <div>
                            <div className="font-semibold text-base">
                              {item?.short_name}
                            </div>
                            <div className="text-gray-500 text-sm">
                              {item?.clienttype}
                            </div>
                          </div>

                          <div className="text-base text-gray-600">
                            {Number(item.finish_width)}''
                          </div>
                        </div>

                        {/* <div className="flex items-baseline gap-2">
                          <div className="text-xl font-bold">₹{item.rate}</div>
                          <div className="text-gray-500 text-sm">/ meter</div>
                        </div> */}
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
            {productData?.length == 0 && (
              <>
                <div className="flex justify-center">
                  <div>
                    <img src="/assets/icons/EmptySearch.svg"></img>
                    <p className="text-[#000] mb-8">
                      Sorry! We couldn't find any matches.
                    </p>
                  </div>
                </div>
              </>
            )}
          </>

          {/* <div className="my-4 flex w-full justify-end">
            <Pagination total={totalProducts} limit={perPage} page={page} onChange={handleChange} />
          </div> */}
        </div>

        <div ref={loaderRef} className="flex justify-center py-6">
          {(loading || isFetchingMore) && <Loader size={40} />}
        </div>
      </>
    </>
  );
};

export default CategoryProducts;
